import { inject, Injectable, signal } from '@angular/core';

import { BillingPlanTier } from '@core/billing-types';
import { FrontendOnlyGuardedFeature } from '@core/feature-guard-legacy';
import { I18nService } from '@core/i18n-legacy';
import { IntegrationKinds } from '@core/integrations-types';
import { SelectGroupedOptions, SelectGroupOption } from '@core/user-interface-legacy';
import {
  AutomationActionType,
  AutomationTriggerType,
  TriggerCategory,
  WorkflowAutomationOptionsData,
} from '@core/workflow-automations-types';
import {
  ActionOption,
  ConditionOption,
  TriggerOptionData,
} from '@recruitee/workflow-automations-types';

import { AutomationAccessService } from './automation-access.service';
import { AutomationsAccessoryDataService } from './automations-accessory-data.service';
import { AutomationsFeatureGuardService } from './automations-feature-guard.service';

type TriggerActionsMap = Record<AutomationTriggerType, ActionOption[]>;

type TriggerConditionsMap = Record<AutomationTriggerType, ConditionOption[]>;

type TriggerDataOptionsMap = Record<AutomationTriggerType, TriggerOptionData[]>;
type TriggerSelectGroupedOptions = SelectGroupedOptions & {
  category: TriggerCategory;
};

@Injectable()
export class AutomationOptionsService {
  private readonly i18n = inject(I18nService);
  private readonly accessoryDataService = inject(AutomationsAccessoryDataService);
  private readonly accessService = inject(AutomationAccessService);
  private readonly automationsFeatureGuardService = inject(AutomationsFeatureGuardService);

  private readonly allPlansActions = [
    AutomationActionType.CandidateSendEmail,
    AutomationActionType.CandidateAddNote,
    AutomationActionType.CandidateAddTask,
    AutomationActionType.CandidateAddTags,
    AutomationActionType.CandidateEvaluationRequest,
    AutomationActionType.CandidateAssignTo,
    AutomationActionType.CandidateAddFollowers,
  ];
  private readonly advancePlanActions = [
    AutomationActionType.CandidateSendToTellentHR,
    AutomationActionType.CandidateUnassignFromJob,
    AutomationActionType.CandidateCancelScheduledEvents,
    AutomationActionType.CandidateCancelEvaluationRequests,
  ];

  public triggerOptions = signal<TriggerSelectGroupedOptions[]>([]);
  public currentTriggerDataOptions = signal<TriggerOptionData[] | null>(null);
  public currentConditionsOptions = signal<ConditionOption[]>([]);
  public currentActionOptions = signal<ActionOption[]>([]);
  public allActionsDataMap = signal<Record<AutomationActionType, ActionOption> | null>(null);

  private triggerConditionsMap = signal<TriggerConditionsMap | null>(null);
  private triggerActionsMap = signal<TriggerActionsMap | null>(null);
  private triggerDataOptionsMap = signal<TriggerDataOptionsMap | null>(null);
  private groupTranslations: Record<TriggerCategory, string> = {
    [TriggerCategory.Candidate]: this.i18n.translate('app.common.candidate'),
    [TriggerCategory.Job]: this.i18n.translate('app.plurals.job.one'),
  };
  private translations = {
    noTellentIntegrationTooltip: this.i18n.translate(
      'app.workflow_automations.automations_wizard_dialog.actions.send_to_tellent_hr.no_integration_tooltip',
    ),
  };

  public setAllOptionsData(optionsData: WorkflowAutomationOptionsData) {
    this.setAllActionOptionsMap(optionsData);
    this.setTriggerOptions(optionsData);
    this.setTriggerConditionsMap(optionsData);
    this.setTriggerActionsMap(optionsData);
    this.setTriggerDataOptionsMap(optionsData);
  }

  public setCurrentOptionsData(triggerType: AutomationTriggerType): void {
    this.currentTriggerDataOptions.set(this.getTriggerDataOptions(triggerType));
    this.currentActionOptions.set(this.getActionOptions(triggerType));
    this.currentConditionsOptions.set(this.getConditionsOptions(triggerType));
  }

  public getGroupTranslation(category: TriggerCategory): string {
    return this.groupTranslations[category];
  }

  private getConditionsOptions(triggerType: AutomationTriggerType): ConditionOption[] {
    return this.triggerConditionsMap()?.[triggerType] || [];
  }

  private getActionOptions(triggerType: AutomationTriggerType): ActionOption[] {
    return this.triggerActionsMap()?.[triggerType] || [];
  }

  private getTriggerDataOptions(triggerType: AutomationTriggerType): TriggerOptionData[] | null {
    return this.triggerDataOptionsMap()?.[triggerType] || null;
  }

  private setTriggerOptions(optionsData: WorkflowAutomationOptionsData) {
    if (this.accessService.triggersHaveUpsell) {
      const availableTriggers = optionsData.filter(trigger => !trigger.isUpsell);
      const upsellTriggers = optionsData.filter(trigger => trigger.isUpsell);
      const triggerGroupSelectOptions: TriggerSelectGroupedOptions[] = [
        {
          title: null,
          options: availableTriggers.map(trigger => ({
            value: trigger.type,
            icon: trigger.iconName,
            label: trigger.name,
          })),
          category: TriggerCategory.Candidate,
        },
        ...(upsellTriggers.length
          ? [
              {
                id: 'upsell',
                title: this.i18n.translate(
                  'app.workflow_automations.automations_wizard_dialog.available_in_optimize',
                ),
                options: upsellTriggers.map(trigger => ({
                  value: trigger.type,
                  icon: trigger.iconName,
                  label: trigger.name,
                  disabled: true,
                  clickHandler: () =>
                    this.automationsFeatureGuardService.getAutomationsFeatureGuardDialog(
                      FrontendOnlyGuardedFeature.WorkflowAutomationsOptimize,
                    ),
                })),
                category: TriggerCategory.Candidate,
              },
            ]
          : []),
      ];
      this.triggerOptions.set(triggerGroupSelectOptions);
    } else {
      const triggerGroupsMap = optionsData.reduce(
        (acc, trigger) => {
          const { category, type: value, iconName: icon, name: label } = trigger;
          acc[category].push({ value, icon, label });
          return acc;
        },
        <Record<TriggerCategory, SelectGroupOption[]>>{
          [TriggerCategory.Candidate]: [],
          [TriggerCategory.Job]: [],
        },
      );
      const triggerGroupSelectOptions: TriggerSelectGroupedOptions[] = Object.entries(
        triggerGroupsMap,
      ).map(([category, options]) => ({
        title: this.groupTranslations[<TriggerCategory>category],
        options,
        category: <TriggerCategory>category,
      }));
      this.triggerOptions.set(triggerGroupSelectOptions);
    }
  }

  private setTriggerConditionsMap(optionsData: WorkflowAutomationOptionsData) {
    this.triggerConditionsMap.set(
      optionsData.reduce(
        (acc, trigger) => {
          acc[trigger.type] = trigger.conditions.map(condition => ({
            type: condition.type,
            label: condition.name,
            options: condition.dataOptions || [],
          }));
          return acc;
        },
        <TriggerConditionsMap>{},
      ),
    );
  }

  private setTriggerActionsMap(optionsData: WorkflowAutomationOptionsData) {
    this.triggerActionsMap.set(
      optionsData.reduce(
        (acc, trigger) => {
          acc[trigger.type] = trigger.actions.map(action => this.allActionsDataMap()![action.type]);
          return acc;
        },
        <TriggerActionsMap>{},
      ),
    );
  }

  private setTriggerDataOptionsMap(optionsData: WorkflowAutomationOptionsData) {
    this.triggerDataOptionsMap.set(
      optionsData.reduce(
        (acc, trigger) => {
          if (trigger.dataOptions?.length) {
            acc[trigger.type] = trigger.dataOptions.map(dataOption => ({
              label: dataOption.label,
              name: dataOption.name,
              options: dataOption.options,
            }));
          }
          return acc;
        },
        <TriggerDataOptionsMap>{},
      ),
    );
  }

  private setAllActionOptionsMap(optionsData: WorkflowAutomationOptionsData) {
    this.allActionsDataMap.set(
      optionsData.reduce(
        (acc, trigger) => {
          trigger.actions.forEach(action => {
            if (acc[action.type]) return;
            acc[action.type] = {
              type: action.type,
              label: action.name,
              icon: action.iconName || '',
              recommended: action.recommended,
              available: action.available,
              entitledFrom: this.getActionEntitledFrom(action.type),
              entitled: action.entitled,
              enableActionTooltip: !action.available
                ? this.i18n.translate(
                    'app.workflow_automations.automations_wizard_dialog.actions.no_permission_to_enable_action',
                  )
                : '',
              editActionTooltip: !action.available
                ? this.i18n.translate(
                    'app.workflow_automations.automations_wizard_dialog.actions.no_permission_to_edit_action',
                  )
                : '',
            };
            if (action.type === AutomationActionType.CandidateSendToTellentHR && action.available) {
              const hasTellentHrIntegrationEnabled = this.accessoryDataService
                .enabledIntegrations()
                ?.includes(IntegrationKinds.KIWI_HR);
              acc[action.type].available = !!hasTellentHrIntegrationEnabled;
              acc[action.type].enableActionTooltip = !hasTellentHrIntegrationEnabled
                ? this.translations.noTellentIntegrationTooltip
                : '';
            }
          });
          return acc;
        },
        <Record<AutomationActionType, ActionOption>>{},
      ),
    );
  }

  private getActionEntitledFrom(actionType: AutomationActionType): BillingPlanTier | null {
    if (this.allPlansActions.includes(actionType)) {
      return null;
    }
    if (this.advancePlanActions.includes(actionType)) {
      return BillingPlanTier.Grow;
    }
    return BillingPlanTier.Optimize;
  }
}
